import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalContent } from '@increasecard/typed-components';
import { useCountry, Country, useEventTracker } from '@increasecard/increase-core';
import { CredentialAuthForm, Constants } from '@card/commonlib';
import {
  CredentialFormParameters,
  CredRow,
  CredRowStatuses,
  INITIAL_CREDROW,
  CredRowErrors
} from '@card/types';

import { CredentialsActions } from './actions';
import { CredentialState } from './reducer';

const { Providers } = Constants.Providers;

type CredentialModalProps = {
  showCredentialModal: boolean;
  toggleCredentialModal: () => void;
  credentialToEdit: CredRow;
  credentials: CredentialState;
};

export default function CredentialModal({
  showCredentialModal,
  toggleCredentialModal,
  credentialToEdit,
  credentials
}: CredentialModalProps) {
  const { t } = useTranslation();
  const tracker = useEventTracker();
  const [credential, setCredential] = useState(INITIAL_CREDROW);
  const dispatch = useDispatch();
  const { credentialErrors, credentialStatuses } = Constants.Credentials;
  const country = useCountry() as Country;

  // Why set the prop value in the state using a useEffect?
  // Is there any logical reason for this?
  useEffect(() => {
    setCredential(credentialToEdit);
  }, [credentialToEdit]);

  useEffect(() => {
    if (!showCredentialModal || !credentials.showPassword) {
      return;
    }
    setCredential({
      ...credential,
      password: credentials.showPassword.password
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials.showPassword]);

  const onChangeField = (value: string, field: keyof CredentialFormParameters) => {
    const credentialToSave = {
      ...(field === 'info_provider' ? INITIAL_CREDROW : credential),
      [field]: value
    };

    if (credentials.credentialError !== credentialErrors.NULL)
      dispatch(CredentialsActions.setError(credentialErrors.NULL as keyof CredRowErrors));

    setCredential(credentialToSave);
  };

  const validateCredential = () => {
    /* eslint-disable @typescript-eslint/camelcase */
    tracker.track('Validate Credential', {
      info_provider: credential.info_provider,
      credential_id: credential.credential_id
    });
    /* eslint-enable @typescript-eslint/camelcase */
    if (credential.info_provider === Providers.MERCADOPAGO) {
      if (credentials.mercadopagoActivationUrl) {
        // Change href so the user is forced to go back an reload the app
        // We should research if MP can send a postMessage to inform registration
        window.location.href = credentials.mercadopagoActivationUrl;
      }
    } else if (credential.credential_id) {
      dispatch(
        CredentialsActions.saveEditedCredential({
          password: credential.password,
          idCredential: credential.credential_id
        })
      );
    } else {
      dispatch(CredentialsActions.validate(credential));
    }
  };

  const closeModal = () => {
    toggleCredentialModal();
    if (!credential.credential_id) setCredential(INITIAL_CREDROW);
    dispatch(CredentialsActions.setStatus(credentialStatuses.EMPTY as keyof CredRowStatuses));
    dispatch(CredentialsActions.setError(credentialErrors.NULL as keyof CredRowErrors));
    if (credentials.credentialChanged) dispatch(CredentialsActions.fetch());
  };

  const onShowPassword = () => {
    if (
      credential.password === INITIAL_CREDROW.password &&
      credential.status === credentialStatuses.CREATED
    ) {
      dispatch(CredentialsActions.getPassword(credential.credential_id));
    }
  };

  if (!showCredentialModal) {
    return null;
  }

  return (
    <Modal
      onCancel={toggleCredentialModal}
      onClose={closeModal}
      onOk={toggleCredentialModal}
      visible={showCredentialModal}
      width="420"
    >
      <ModalHeader align="left">
        {credential.credential_id
          ? t('abm:Shops:EditCredentialModal:titleEditCredential')
          : t('abm:Shops:EditCredentialModal:titleNewCredential')}
      </ModalHeader>
      <ModalContent>
        <CredentialAuthForm
          country={country}
          credential={credential}
          credentialError={credentials.credentialError}
          credentialStatus={credentials.credentialStatus}
          layout="column"
          onChangeField={onChangeField}
          onShowPassword={onShowPassword}
          showInfoLink
          translateFunction={t}
          validateCredential={validateCredential}
        />
      </ModalContent>
    </Modal>
  );
}
